import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";

interface Supply {
  _id: Id<"locationSupplies">;
  supplyId?: Id<"supplies">;
  name: string;
  quantity: number;
  unit: string;
  lastRestocked: string;
  minimumRequired?: number;
  brand?: string;
  vendor?: string;
  costPerUnit?: number;
  storageLocation?: string;
  category?: string;
}

interface EditSuppliesSheetProps {
  isOpen: boolean;
  onClose: () => void;
  supply: Supply | null;
  onSuppliesUpdated?: () => void;
}

export function EditSuppliesSheet({
  isOpen,
  onClose,
  supply,
  onSuppliesUpdated,
}: EditSuppliesSheetProps) {
  const [formData, setFormData] = useState({
    name: "",
    quantity: 0,
    unit: "",
    lastRestocked: "",
    minimumRequired: 0,
    brand: "",
    vendor: "",
    costPerUnit: 0,
    storageLocation: "",
    category: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const masterSupplies = useQuery(api.supplies.list) || [];
  const updateSupplies = useMutation(api.serviceLocations.updateLocationSupplies);

  // Check if this supply is linked to a master supply
  const isLinkedToMaster = !!supply?.supplyId;
  const linkedMasterSupply = isLinkedToMaster
    ? masterSupplies.find((s: any) => s._id === supply?.supplyId)
    : null;

  // Populate form when supply changes
  useEffect(() => {
    if (supply) {
      setFormData({
        name: supply.name || "",
        quantity: supply.quantity || 0,
        unit: supply.unit || "",
        lastRestocked: supply.lastRestocked || "",
        minimumRequired: supply.minimumRequired || 0,
        brand: supply.brand || "",
        vendor: supply.vendor || "",
        costPerUnit: supply.costPerUnit || 0,
        storageLocation: supply.storageLocation || "",
        category: supply.category || "",
      });
    }
  }, [supply]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!supply) {
      toast({
        title: "Error",
        description: "No supply selected for editing",
        variant: "destructive",
      });
      return;
    }

    if (!formData.name.trim() || !formData.unit.trim() || !formData.lastRestocked) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await updateSupplies({
        supplyId: supply._id,
        name: formData.name.trim(),
        quantity: formData.quantity,
        unit: formData.unit.trim(),
        lastRestocked: formData.lastRestocked,
        minimumRequired: formData.minimumRequired > 0 ? formData.minimumRequired : undefined,
        brand: formData.brand.trim() || undefined,
        vendor: formData.vendor.trim() || undefined,
        costPerUnit: formData.costPerUnit > 0 ? formData.costPerUnit : undefined,
        storageLocation: formData.storageLocation.trim() || undefined,
        category: formData.category || undefined,
      });

      toast({
        title: "Success!",
        description: "Supplies updated successfully",
      });
      onSuppliesUpdated?.();
      onClose();
    } catch (error) {
      console.error("Error updating supplies:", error);
      toast({
        title: "Error",
        description: "Failed to update supplies",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Get today's date in YYYY-MM-DD format for max value
  const today = new Date().toISOString().split("T")[0];

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Edit Supplies</SheetTitle>
          <SheetDescription>Update the supply information for this location.</SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] pr-6">
          <form onSubmit={handleSubmit} className="space-y-6 py-6">
            {/* Show linked supply info if applicable */}
            {isLinkedToMaster && linkedMasterSupply && (
              <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-900">
                <p className="font-medium">Linked to Master Supply:</p>
                <p>{linkedMasterSupply.itemName}</p>
              </div>
            )}

            {/* Supply Information (from Master List) */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Supply Information {isLinkedToMaster && "(from Master List)"}
              </h3>

              <div className="space-y-2">
                <Label htmlFor="name">
                  Item Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., All-Purpose Cleaner, Paper Towels"
                  required
                  disabled={isLinkedToMaster}
                  className={isLinkedToMaster ? "bg-muted" : ""}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Cleaning Chemicals"
                  disabled={isLinkedToMaster}
                  className={isLinkedToMaster ? "bg-muted" : ""}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand</Label>
                  <Input
                    id="brand"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="e.g., Clorox"
                    disabled={isLinkedToMaster}
                    className={isLinkedToMaster ? "bg-muted" : ""}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vendor">Vendor</Label>
                  <Input
                    id="vendor"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="e.g., ABC Supply Co."
                    disabled={isLinkedToMaster}
                    className={isLinkedToMaster ? "bg-muted" : ""}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="unit">
                    Unit <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="unit"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    placeholder="e.g., gal, box, case"
                    required
                    disabled={isLinkedToMaster}
                    className={isLinkedToMaster ? "bg-muted" : ""}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="costPerUnit">Cost Per Unit</Label>
                  <Input
                    id="costPerUnit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.costPerUnit}
                    onChange={(e) =>
                      setFormData({ ...formData, costPerUnit: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="0.00"
                    disabled={isLinkedToMaster}
                    className={isLinkedToMaster ? "bg-muted" : ""}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Location-Specific Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Location-Specific Details
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">
                    Quantity on Hand <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="0"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="minimumRequired">Reorder Level</Label>
                  <Input
                    id="minimumRequired"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.minimumRequired}
                    onChange={(e) =>
                      setFormData({ ...formData, minimumRequired: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastRestocked">
                  Last Restocked <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastRestocked"
                  type="date"
                  value={formData.lastRestocked}
                  onChange={(e) => setFormData({ ...formData, lastRestocked: e.target.value })}
                  max={today}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="storageLocation">Storage Location</Label>
                <Input
                  id="storageLocation"
                  value={formData.storageLocation}
                  onChange={(e) => setFormData({ ...formData, storageLocation: e.target.value })}
                  placeholder="e.g., Storage Room A, Shelf 3"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? "Updating..." : "Update Supplies"}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
